// src/pages/Users/Techniques/components/TechniqueCard.jsx

import React from "react";

import {
  FaEye,
  FaVolumeUp,
} from "react-icons/fa";

import useTechniqueSpeech from "../hooks/useTechniqueSpeech";

import {
  FALLBACK_TECHNIQUE_IMG,
  beltTheme,
  resolveTechniqueAssetUrl,
  truncateText,
} from "../utils/techniquesUtils";

import { speakJapanese } from "../utils/speechUtils";

function handleImageError(event) {
  const image = event.currentTarget;

  if (
    image.dataset.fallbackApplied ===
    "true"
  ) {
    return;
  }

  image.dataset.fallbackApplied =
    "true";

  image.src =
    FALLBACK_TECHNIQUE_IMG;
}

export default function TechniqueCard({
  technique,
  onOpen,
}) {
  const {
    image1_url,
    nom,
    nom_japonais,
    famille,
    traduction,
    ceinture,
    information,
  } = technique || {};

  const imageUrl =
    resolveTechniqueAssetUrl(
      image1_url,
    ) || FALLBACK_TECHNIQUE_IMG;

  const theme =
    beltTheme(ceinture);

  const speechText =
    useTechniqueSpeech(
      nom_japonais || nom,
    );

  const informationPreview =
    truncateText(information, 105);

  const handleSpeak = () => {
    speakJapanese(speechText);
  };

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[1.65rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700">
      <button
        type="button"
        onClick={onOpen}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-950"
        aria-label={`Ouvrir la technique ${nom || ""}`}
      >
        <div className="aspect-[551/617] w-full overflow-hidden">
          <img
            src={imageUrl}
            alt={
              nom ||
              "Technique de judo"
            }
            loading="lazy"
            onError={handleImageError}
            className="h-full w-full object-contain p-1.5 transition duration-500 group-hover:scale-[1.035]"
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950/45 to-transparent opacity-60" />

        <div className="absolute left-2 top-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wide ring-1 ${theme.badge}`}
          >
            {ceinture || "Ceinture"}
          </span>
        </div>

        {famille ? (
          <div className="absolute bottom-2 left-2 right-2">
            <span className="inline-flex max-w-full rounded-full bg-slate-950/75 px-2.5 py-1 text-[9px] font-bold text-white backdrop-blur">
              <span className="truncate">
                {famille}
              </span>
            </span>
          </div>
        ) : null}
      </button>

      <div className="flex flex-1 flex-col p-3">
        <div className="flex-1">
          <h2 className="line-clamp-2 text-sm font-black leading-snug text-slate-950 dark:text-white sm:text-[15px]">
            {nom ||
              "Technique sans nom"}
          </h2>

          {traduction ? (
            <p className="mt-1 line-clamp-2 text-[11px] italic leading-4 text-slate-500 dark:text-slate-400">
              {traduction}
            </p>
          ) : null}

          {informationPreview ? (
            <p className="mt-2 hidden line-clamp-3 text-xs leading-5 text-slate-600 dark:text-slate-300 sm:block">
              {informationPreview}
            </p>
          ) : null}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleSpeak}
            disabled={!speechText}
            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-2xl bg-slate-100 px-2 text-[11px] font-bold text-slate-700 transition hover:bg-slate-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:text-xs"
          >
            <FaVolumeUp />
            Écouter
          </button>

          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-2xl bg-slate-950 px-2 text-[11px] font-bold text-white transition hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 sm:text-xs"
          >
            <FaEye />
            Voir
          </button>
        </div>
      </div>
    </article>
  );
}